import React from "react";
import {
  Target, DollarSign, Briefcase, TrendingUp, AlertTriangle, Scissors, Check,
} from "lucide-react";
import DemandPageHeader from "../components/DemandPageHeader";
import { get } from "../api/http";
import { useDemandId } from "../context/DemandContext";

/**
 * Portfolio management: Priority Engine, BudgetAligner, IT Investment.
 *
 * One component with three tabs rather than three pages — they share the same
 * ranked-demand data, and the argument only lands when you can move between them:
 * rank the queue, see where the money runs out, see what the funded work becomes.
 *
 * Costs and budget envelopes are mock and badged as such; priority scores are
 * computed from real demand rows and the funding sources are real TRM data.
 */

const CARD = "rounded-2xl border border-gray-200 bg-white shadow-sm";
const money = (n) =>
  n === null || n === undefined ? "—"
  : Math.abs(n) >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : `$${Math.round(n / 1000)}K`;

function MockBadge({ children = "mock" }) {
  return (
    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
      {children}
    </span>
  );
}
function RealBadge() {
  return (
    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
      live data
    </span>
  );
}

const WEIGHT_LABELS = {
  strategic_alignment: "Strategic alignment",
  urgency: "Urgency",
  readiness: "Readiness (triage)",
  confidence: "Discovery confidence",
  risk: "Security posture",
};

/* ------------------------------------------------------------ Priority */
function PriorityTab({ onPick }) {
  const [weights, setWeights] = React.useState({
    strategic_alignment: 30, urgency: 20, readiness: 20, confidence: 15, risk: 15,
  });
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    const qs = new URLSearchParams(weights).toString();
    get(`/portfolio/priority/ranking?${qs}`)
      .then((d) => { if (!cancelled) { setData(d); setErr(""); } })
      .catch((e) => { if (!cancelled) setErr(e.message); });
    return () => { cancelled = true; };
  }, [weights]);

  const total = Object.values(weights).reduce((a, b) => a + b, 0);

  return (
    <div className="grid gap-5 lg:grid-cols-4">
      <section className={`${CARD} p-5 lg:col-span-1`}>
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-base text-gray-900">Scoring model</h3>
        </div>
        <p className="mb-4 text-xs text-gray-500">
          Weights are relative; they need not sum to 100. Ranking updates as you drag.
        </p>
        {Object.entries(weights).map(([k, v]) => (
          <div key={k} className="mb-3">
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="text-gray-700">{WEIGHT_LABELS[k]}</span>
              <span className="tabular-nums font-semibold text-gray-900">
                {total ? Math.round((v / total) * 100) : 0}%
              </span>
            </div>
            <input
              type="range" min="0" max="100" value={v}
              onChange={(e) => setWeights((w) => ({ ...w, [k]: Number(e.target.value) }))}
              className="w-full accent-indigo-600"
            />
          </div>
        ))}
        <button
          onClick={() => setWeights({ strategic_alignment: 30, urgency: 20, readiness: 20, confidence: 15, risk: 15 })}
          className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          Reset to default
        </button>
      </section>

      <section className={`${CARD} lg:col-span-3`}>
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <h3 className="text-base text-gray-900">Ranked demand queue</h3>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            {data && <span>{data.count} demands · {money(data.totalEstimatedCost)}</span>}
            <MockBadge>mock costs</MockBadge>
          </div>
        </div>
        {err && <div className="p-4 text-sm text-red-700">{err}</div>}
        <div style={{ overflowX: "auto" }}>
          <table className="min-w-full text-sm" style={{ width: "100%" }}>
            <thead className="bg-gray-50 text-xs text-gray-500">
              <tr>
                {["#", "Demand", "Score", "Breakdown", "Est. cost", "Value / $M"].map((h) => (
                  <th key={h} className="px-4 py-2 text-left font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.ranking || []).map((r) => (
                <tr key={r.demand_id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2 text-lg font-bold tabular-nums text-gray-400">{r.rank}</td>
                  <td className="px-4 py-2">
                    <button onClick={() => onPick(r.demand_id)}
                            className="text-left font-medium text-indigo-600 hover:underline">
                      {r.title?.slice(0, 40) || r.demand_id}
                    </button>
                    <div className="text-xs text-gray-500">
                      {r.demand_id} · {r.directorate || "—"} · {r.currentStage}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-sm font-bold tabular-nums text-indigo-700">
                      {r.score}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {/* Stacked bar shows WHY a demand ranks where it does — a bare
                        score is not defensible in a portfolio meeting. */}
                    <div className="flex h-2 w-40 overflow-hidden rounded-full bg-gray-100">
                      {Object.entries(r.components).map(([k, v], i) => (
                        <div key={k} title={`${WEIGHT_LABELS[k]}: ${v}`}
                             style={{
                               width: `${v / 5}%`,
                               background: ["var(--cares-purple)", "var(--cares-teal)", "var(--cares-green)", "var(--cares-amber)", "var(--cares-purple)"][i],
                             }} />
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 tabular-nums text-gray-700">{money(r.estimatedCost)}</td>
                  <td className="px-4 py-2 tabular-nums text-gray-500">{r.valuePerMillion}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------ Budget */
function BudgetTab() {
  const [applySavings, setApplySavings] = React.useState(false);
  const [extra, setExtra] = React.useState(0);
  const [plan, setPlan] = React.useState(null);
  const [sources, setSources] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    get(`/portfolio/budget/plan?apply_savings=${applySavings}&additional_funding=${extra}`)
      .then((d) => { if (!cancelled) setPlan(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, [applySavings, extra]);

  React.useEffect(() => {
    get("/portfolio/budget/funding-sources").then(setSources).catch(() => {});
  }, []);

  return (
    <div className="space-y-5">
      <section className={`${CARD} p-5`}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base text-gray-900">FY{plan?.fiscalYear || "—"} funding</h3>
          <MockBadge>mock envelopes</MockBadge>
        </div>
        <div className="grid gap-4 sm:grid-cols-4">
          {[
            ["Available", money(plan?.totalAvailable), "text-gray-900"],
            ["Committed", money(plan?.committed), "text-indigo-700"],
            ["Remaining", money(plan?.remaining), "text-emerald-700"],
            ["Unfunded", money(plan?.unfundedCost), "text-red-600"],
          ].map(([label, value, tone]) => (
            <div key={label}>
              <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
              <div className={`mt-1 text-2xl font-semibold tabular-nums ${tone}`}>{value}</div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-4 border-t border-gray-100 pt-4">
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input type="checkbox" checked={applySavings}
                   onChange={(e) => setApplySavings(e.target.checked)}
                   className="h-4 w-4 accent-emerald-600" />
            <span className="font-medium text-gray-800">Apply TRM consolidation savings</span>
            {sources && (
              <span className="text-emerald-700">(+{money(sources.totalAvailable)})</span>
            )}
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            Additional funding
            <input type="number" step="500000" min="0" value={extra}
                   onChange={(e) => setExtra(Number(e.target.value) || 0)}
                   className="w-32 rounded-lg border border-gray-300 px-2 py-1 text-sm tabular-nums" />
          </label>
        </div>
      </section>

      <section className={CARD}>
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <h3 className="text-base text-gray-900">Funding line</h3>
          <span className="text-xs text-gray-500">
            {plan?.fundedCount} funded · {plan?.unfundedCount} below the line
          </span>
        </div>
        <div className="divide-y divide-gray-100">
          {(plan?.funded || []).map((f) => (
            <Row key={f.demand_id} item={f} funded />
          ))}
          {plan?.unfundedCount > 0 && (
            <div className="flex items-center gap-2 bg-red-50 px-5 py-2 text-xs font-semibold uppercase tracking-wide text-red-700">
              <Scissors size={14} /> Cut line — {money(plan.unfundedCost)} unfunded
            </div>
          )}
          {(plan?.unfunded || []).map((f) => (
            <Row key={f.demand_id} item={f} />
          ))}
        </div>
      </section>

      <section className={CARD}>
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <h3 className="text-base text-gray-900">Funding sources — consolidation savings</h3>
          <RealBadge />
        </div>
        <div className="divide-y divide-gray-100">
          {(sources?.sources || []).slice(0, 6).map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-3 px-5 py-2.5">
              <TrendingUp size={15} className="shrink-0 text-emerald-600" />
              <span className="min-w-0 flex-1 truncate text-sm text-gray-800">
                Retire <strong>{s.retire}</strong> → {s.consolidateTo}
              </span>
              <span className="text-xs text-gray-500">ROI {s.roiMonths}mo</span>
              <span className="tabular-nums text-sm font-semibold text-emerald-700">
                {money(s.annualSavings)}/yr
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Row({ item, funded }) {
  return (
    <div className="flex flex-wrap items-center gap-3 px-5 py-2.5">
      <span className={`w-7 shrink-0 text-center text-sm font-bold tabular-nums ${
        funded ? "text-emerald-600" : "text-gray-400"}`}>
        {item.rank}
      </span>
      {funded ? <Check size={15} className="shrink-0 text-emerald-600" />
              : <AlertTriangle size={15} className="shrink-0 text-red-500" />}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-gray-900">
          {item.title || item.demand_id}
        </div>
        <div className="text-xs text-gray-500">
          {item.demand_id} · score {item.score} · {item.directorate || "—"}
        </div>
      </div>
      <span className="tabular-nums text-sm text-gray-700">{money(item.estimatedCost)}</span>
      {funded && item.cumulativeCost !== undefined && (
        <span className="w-20 text-right tabular-nums text-xs text-gray-400">
          {money(item.cumulativeCost)}
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ Investments */
function InvestmentTab({ onPick }) {
  const [data, setData] = React.useState(null);
  React.useEffect(() => { get("/portfolio/investments").then(setData).catch(() => {}); }, []);

  const tone = { Planned: "amber", Funded: "blue", "In Execution": "emerald" };
  const cls = {
    amber: "bg-amber-50 text-amber-800 ring-amber-200",
    blue: "bg-blue-50 text-blue-800 ring-blue-200",
    emerald: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">
          FY{data?.fiscalYear} · {data?.count} investments · {money(data?.totalCost)}
        </span>
        <MockBadge>mock investments</MockBadge>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {(data?.investments || []).map((inv) => (
          <section key={inv.investment_id} className={`${CARD} p-5`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-mono text-xs text-gray-500">{inv.investment_id}</div>
                <h3 className="mt-0.5 text-base leading-snug text-gray-900">{inv.name}</h3>
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${cls[tone[inv.lifecycle]] || cls.amber}`}>
                {inv.lifecycle}
              </span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-gray-600">{inv.objective}</p>
            <div className="mt-3 grid grid-cols-2 gap-2 border-t border-gray-100 pt-3 text-xs">
              <div><span className="text-gray-500">UII</span><div className="font-mono text-gray-800">{inv.uii}</div></div>
              <div><span className="text-gray-500">Owner</span><div className="text-gray-800">{inv.owner}</div></div>
              <div><span className="text-gray-500">Appropriation</span><div className="text-gray-800">{inv.appropriation}</div></div>
              <div><span className="text-gray-500">Total</span><div className="font-semibold tabular-nums text-gray-900">{money(inv.totalCost)}</div></div>
            </div>
            <div className="mt-3 border-t border-gray-100 pt-3">
              <div className="mb-1.5 text-[11px] uppercase tracking-wide text-gray-500">
                Demands ({inv.demandCount})
              </div>
              {inv.demands.map((d) => (
                <button key={d.demand_id} onClick={() => onPick(d.demand_id)}
                        className="mb-1 block w-full truncate rounded px-2 py-1 text-left text-xs text-indigo-700 hover:bg-indigo-50">
                  {d.demand_id} — {d.title?.slice(0, 30)} {d.handedOff && "✓"}
                </button>
              ))}
              {inv.missingDemands?.length > 0 && (
                <div className="mt-1 text-[11px] text-amber-700">
                  {inv.missingDemands.length} demand(s) referenced but not found locally
                </div>
              )}
            </div>
          </section>
        ))}
      </div>

      {data?.unassignedDemands?.length > 0 && (
        <section className={`${CARD} p-5`}>
          <h3 className="mb-1 text-base text-gray-900">Not yet in an investment</h3>
          <p className="mb-3 text-xs text-gray-500">
            Approved work with no CPIC home — these have no funding line and no
            reporting parent.
          </p>
          <div className="flex flex-wrap gap-2">
            {data.unassignedDemands.map((d) => (
              <button key={d.demand_id} onClick={() => onPick(d.demand_id)}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:border-indigo-300 hover:bg-indigo-50">
                {d.demand_id} · {d.title?.slice(0, 28)}
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ shell */
const TABS = [
  { id: "priority", label: "Priority Engine", icon: Target },
  { id: "budget", label: "BudgetAligner", icon: DollarSign },
  { id: "investments", label: "IT Investment", icon: Briefcase },
];

export default function PortfolioPage({ initialTab = "priority" }) {
  const [tab, setTab] = React.useState(initialTab);
  const { setDemandId } = useDemandId();
  React.useEffect(() => { setTab(initialTab); }, [initialTab]);

  const meta = {
    priority: ["Priority Engine", "Rank the demand queue against a weighted, adjustable model"],
    budget: ["BudgetAligner", "Fit the ranked queue inside the funding available — and find the money"],
    investments: ["IT Investment & Project Initiation", "Group funded demands into reportable investments"],
  }[tab];

  return (
    <div>
      <DemandPageHeader title={meta[0]} description={meta[1]} breadcrumbPath={meta[0]} />
      <div className="mx-auto max-w-7xl px-2 pb-10">
        <div className="mb-5 inline-flex rounded-xl bg-gray-100 p-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
                    className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition ${
                      tab === t.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "priority" && <PriorityTab onPick={setDemandId} />}
        {tab === "budget" && <BudgetTab />}
        {tab === "investments" && <InvestmentTab onPick={setDemandId} />}
      </div>
    </div>
  );
}
